/**
 * `email` — `EmailValue` (`specs/M2-tier2-column-types.md` §5.9, `[09§A.7 email]`, `[cvs§2 email]`).
 *
 * `value` is `{"email":"…","text":"…","changed_at":…}`; the read fragment calls `text` **`label`**
 * `[09§A.7 rule 3]`. Two `toText` shapes, both measured in the 2026-09-10 snapshot: when the
 * label equals the address the text is the bare address (`"Phoenix@email.com"`), and when it
 * differs it is `"Work - sarah.j@example.com"` — `"<label> - <email>"`.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const emailValueSchema = z.looseObject({
    email: z.string(),
    /** The display label; equals the address when the user typed none `[cvs§2 email]`. */
    text: z.string().optional(),
    changed_at: z.string().nullable().optional(),
});
import { emailFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const emailSettingsSchema = z.looseObject({
    includePulseInSubject: z.boolean().optional(),
    ccPulse: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
/**
 * Deliberately permissive: monday stores whatever the UI accepted and this account holds
 * addresses that a strict RFC check would reject. The editor validates (`@`-shaped, per the
 * capture); the value pipeline only refuses a *shape* it cannot store.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+$/;
/** `change_simple_column_value`: `"example@example.com This is an example email"` `[09§A.7]`. */
function fromSimple(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const space = trimmed.indexOf(' ');
    if (space < 0)
        return { email: trimmed, text: trimmed };
    return { email: trimmed.slice(0, space), text: trimmed.slice(space + 1).trim() };
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return fromSimple(raw);
    if (typeof raw !== 'object') {
        throw new ColumnValueError('email', `expected an object or string, received ${typeof raw}`);
    }
    const record = raw;
    const email = record.email;
    if (email === null || email === undefined || email === '')
        return null;
    if (typeof email !== 'string')
        throw new ColumnValueError('email', '"email" must be a string');
    if (!EMAIL_RE.test(email))
        throw new ColumnValueError('email', `"${email}" is not an email address`);
    const value = { email };
    const label = typeof record.text === 'string' ? record.text : record.label;
    value.text = typeof label === 'string' && label !== '' ? label : email;
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
function renderText(value) {
    if (value === null)
        return '';
    const label = value.text ?? '';
    return label === '' || label === value.email ? value.email : `${label} - ${value.email}`;
}
export const emailColumn = defineColumnType({
    type: 'email',
    title: 'Email',
    description: 'An email address with an optional display label.',
    apiFragmentName: 'EmailValue',
    legacyTypeName: 'email',
    idPrefix: 'email',
    valueSchema: emailValueSchema,
    settingsSchema: emailSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: emailSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    /** *"`any_of` and `not_any_of` match against the display text (`label`)"* `[09§A.7 email]`. */
    filterConditions: emailFilterConditions,
    filterOperators: operatorsOf(emailFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    parseSimpleValue(raw) {
        return raw === null ? null : fromSimple(raw);
    },
    toText(value) {
        return renderText(value);
    },
    toSortKeys(value) {
        return { text: textShadow(renderText(value)), number: null, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.email === b.email && (a.text ?? a.email) === (b.text ?? b.email);
    },
    toActivityValue(value) {
        return value === null ? null : { email: value.email, text: value.text ?? value.email };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { email: value.email, text: value.text ?? value.email };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue(value) {
        if (value === null)
            return '';
        const label = value.text ?? '';
        return label === '' || label === value.email ? value.email : `${value.email} ${label}`;
    },
    fromMondaySettings(raw) {
        return emailSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return { email: value.email, label: value.text ?? value.email, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        if (value === null)
            return [];
        const label = value.text ?? value.email;
        return label === '' ? [] : [{ key: label, label }];
    },
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map